import { Link } from "react-router-dom";
import { Coffee, Car, UtensilsCrossed, BellRing, Puzzle, HeartHandshake, MapPin, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES } from "@/lib/images";

const SERVICES = [
  {
    icon: Coffee,
    title: "Social Connection & Story Sharing",
    text: "Unhurried conversation over tea, reminiscing over photo albums, reading aloud — the simple pleasure of someone who genuinely wants to hear about their day.",
    testid: "service-card-social",
  },
  {
    icon: Car,
    title: "Transportation, Outings & Errands",
    text: "Friendly rides to appointments, social events and religious services — plus help with grocery shopping and planning fun outings, with a driver they know and trust.",
    testid: "service-card-transportation",
  },
  {
    icon: UtensilsCrossed,
    title: "Light Housekeeping & Meals",
    text: "Help with laundry and keeping spaces comfortable, plus menu planning, grocery shopping and meal preparation — and a companion to share the meal with.",
    testid: "service-card-housekeeping",
  },
  {
    icon: BellRing,
    title: "Daily Living Support & Reminders",
    text: "A steady arm for sitting, standing and walking, plus gentle medication and exercise reminders to keep healthy routines on track.",
    testid: "service-card-reminders",
  },
  {
    icon: Puzzle,
    title: "Games, Crafts & Recreation",
    text: "Cribbage, crosswords, knitting, gardening, music, dance — we match companions to the pastimes your loved one has always loved.",
    testid: "service-card-hobbies",
  },
  {
    icon: HeartHandshake,
    title: "Respite Care & Facility Visits",
    text: "Trusted hours so family caregivers can rest and recharge — and regular one-on-one visits to hospitals, rehab centres and care facilities between family visits.",
    testid: "service-card-respite",
  },
];

const AREAS = ["Calgary", "Airdrie", "Cochrane", "Okotoks", "Chestermere", "Surrounding Areas"];

const STEPS = [
  { num: "01", title: "Book a free consultation", text: "A friendly chat to learn about your loved one's needs, interests and routine." },
  { num: "02", title: "We find the match", text: "We hand-pick a companion whose personality and interests genuinely fit theirs." },
  { num: "03", title: "Regular visits begin", text: "The same companion, a consistent schedule, and updates to your family after every visit." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="eyebrow mb-5" data-testid="services-eyebrow">What We Do</p>
        <h1 className="max-w-3xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="services-heading">
          <MaskedLine delay={0.1}>Companionship, done</MaskedLine>
          <MaskedLine delay={0.25}>
            with <em className="text-brand">heart.</em>
          </MaskedLine>
        </h1>
        <FadeIn delay={0.5}>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-warmgray sm:text-xl">
            Non-medical companion care tailored to your loved one's personality — because a great
            visit looks different for everyone.
          </p>
        </FadeIn>
      </section>

      {/* Service cards */}
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.1}>
              <article
                data-testid={s.testid}
                className="group h-full rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10"
              >
                <div className="mb-5 inline-flex rounded-2xl bg-brand-soft p-4 transition-colors duration-300 group-hover:bg-sage-soft">
                  <s.icon className="h-8 w-8 text-brand transition-colors duration-300 group-hover:text-sage" />
                </div>
                <h3 className="mb-3 font-serif text-xl font-medium text-espresso sm:text-2xl">{s.title}</h3>
                <p className="text-base leading-relaxed text-warmgray">{s.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <p className="rounded-2xl border border-brand/25 bg-brand-soft/60 p-5 text-center text-base font-medium text-espresso sm:text-lg" data-testid="non-medical-note">
            Please note: we are a non-medical companion care service. Companions help with daily
            living and offer medication reminders, but we do not provide nursing, bathing or medical
            procedures — just genuine company and a helpful hand.
          </p>
        </Reveal>
      </section>

      <Marquee items={["Social Connection", "Transportation & Errands", "Meals & Housekeeping", "Gentle Reminders", "Games & Recreation", "Family Respite"]} />

      {/* Where we serve */}
      <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-28">
        <div>
          <Reveal>
            <p className="eyebrow mb-4">Where We Serve</p>
            <h2 className="mb-5 font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Proudly local, across Calgary & area
            </h2>
            <p className="mb-8 max-w-lg text-lg leading-relaxed text-warmgray">
              In private homes, retirement residences, hospitals and care facilities — wherever your
              loved one is, we come to them.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="flex flex-wrap gap-3" data-testid="service-areas-list">
              {AREAS.map((a) => (
                <li key={a} className="flex items-center gap-2 rounded-full border border-espresso/15 bg-white px-5 py-2.5 text-base font-semibold text-espresso shadow-sm">
                  <MapPin className="h-4 w-4 text-brand" /> {a}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-espresso/15">
            <img
              src={IMAGES.servicesBench}
              alt="A senior and companion sitting together on a park bench"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
              data-testid="services-image"
            />
          </div>
        </Reveal>
      </section>

      {/* Steps */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-4">Getting Started</p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Three easy steps to their new friend
            </h2>
          </Reveal>
          <ol className="grid gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.12}>
                <li data-testid={`services-step-${i + 1}`} className="h-full rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10">
                  <span className="font-serif text-5xl font-bold text-brand/30">{step.num}</span>
                  <h3 className="mb-3 mt-4 font-serif text-xl font-medium text-espresso sm:text-2xl">{step.title}</h3>
                  <p className="text-base leading-relaxed text-warmgray">{step.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.3} className="mt-12 text-center">
            <Link to="/contact" data-testid="services-cta-button" className="btn-primary">
              Start with a Free Consultation <ArrowRight className="h-5 w-5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
